// Static board-state evaluator. Returns a single number that's positive
// when `side` is winning, negative when losing. Called at every leaf of
// the SearchAI tree, so cost matters — keep it linear in the number of
// instances on the board.
//
// The score units are calibrated so 1 unit ≈ 1 HP. Weights for the other
// features are tuned in data/ai-tuning/<profile>.json relative to that
// baseline (see normal.json for the default mix).

import type { CardDef, GameState, PlayerSide } from '../types.js';
import { getEffectivePower, getPlayerState, getOpponentSide } from '../engine/GameState.js';
import { hasPassive } from '../engine/AbilityProcessor.js';
import type { EvaluatorWeights } from './types.js';

/** Terminal-state bonus. Larger than any plausible feature contribution so
 *  a winning leaf always outranks any non-terminal leaf. */
const TERMINAL_WIN = 1e9;

/** Per-ply tiebreaker on terminal scores so the search prefers faster wins
 *  and slower losses (chess-engine "mate-in-N"): a terminal reached closer
 *  to the root gets a larger-magnitude bias than a deeper one.
 *
 *  This is deliberately LARGE — larger than any positional eval magnitude —
 *  so that among losing lines, "survive as long as possible" is the PRIMARY
 *  criterion and the positional blend (see the losing-terminal branch below)
 *  is only a sub-tiebreak among lines that die at the same time. Without
 *  this dominance the AI would trade survival for board: e.g. decline to
 *  block a lethal attacker because blocking loses a blocker (more board if it
 *  dies now with the blocker alive), when it should block to live another
 *  turn. Still trivially small vs TERMINAL_WIN (1e9) and vs the
 *  determinization terminal-loss threshold (5e8), so it only orders terminals
 *  within their win/loss tier and never flips a loss above a non-terminal. */
const TERMINAL_PLY_BIAS = 1_000_000;

/** A character that cannot block can never spend its power defensively —
 *  its only path to value is attacking. So as a static board asset it's
 *  worth less than its face power: an idle one contributes nothing it
 *  could realize, which is why the AI used to over-hold berserkers rather
 *  than trade them. Discount their board-power contribution so the search
 *  stops valuing them as durable walls. Kept mild (0.9) so the AI still
 *  happily plays them — the point is to free them to attack, not to
 *  devalue the card. */
const CANNOT_BLOCK_POWER_FACTOR = 0.9;

export function evaluate(
  state: GameState,
  side: PlayerSide,
  weights: EvaluatorWeights,
  catalog: Map<string, CardDef>,
  /** Search-budget plies remaining when this state was reached. Higher
   *  = closer to the root = the outcome happens sooner. Used only to
   *  bias terminal scores toward "win sooner / lose later". Non-terminal
   *  scoring is depth-agnostic. */
  depthRemaining: number,
): number {
  // Winning terminal: close it out as fast as possible. No positional
  // component — a win is a win, and the ply bias makes the search grab the
  // soonest one (less exposure to the determinization being wrong).
  if (state.winner === side) {
    return TERMINAL_WIN + depthRemaining * TERMINAL_PLY_BIAS;
  }

  const us = getPlayerState(state, side);
  const themSide = getOpponentSide(side);
  const them = getPlayerState(state, themSide);

  const sumRingPower = (ps: typeof us): number => {
    let p = 0;
    for (const slot of ps.ring) {
      if (!slot.instanceId) continue;
      const power = getEffectivePower(state, slot.instanceId, catalog);
      p += hasPassive(state, slot.instanceId, 'passive-cannot-block', catalog)
        ? power * CANNOT_BLOCK_POWER_FACTOR
        : power;
    }
    return p;
  };
  const countRing = (ps: typeof us): number => ps.ring.reduce((n, s) => n + (s.instanceId ? 1 : 0), 0);

  // Ring-presence valuation: non-linear curve (indexed by ring count) when
  // the profile provides one, else the flat linear weight.
  const curve = weights.ringPresenceCurve;
  const ringPresenceScore = curve
    ? curveAt(curve, countRing(us)) - curveAt(curve, countRing(them))
    : weights.ringPresence * (countRing(us) - countRing(them));

  const hpDelta = us.hp - them.hp;
  const boardPowerDelta = sumRingPower(us) - sumRingPower(them);
  const handSizeDelta = us.hand.length - them.hand.length;
  const spiritDelta = us.spirit - them.spirit;
  const deckSizeDelta = us.deck.length - them.deck.length;

  const positional =
    weights.hp * hpDelta
    + weights.boardPower * boardPowerDelta
    + weights.handSize * handSizeDelta
    + weights.spirit * spiritDelta
    + ringPresenceScore
    + weights.deckSize * deckSizeDelta;

  // Losing terminal: the loss is unavoidable on this line within the search
  // horizon — but not all losses are equal. A flat -TERMINAL_WIN made every
  // losing line tie, so the AI picked whichever came first in the legal list
  // (usually SKIP_PHASE) and passed its turn while dying. Blend the ordinary
  // positional eval so the search goes down fighting: it prefers the line
  // that deals the most damage, keeps the most board, etc. (e.g. an on-play
  // damage effect dwarfs the hand/spirit terms). The blend stays far below
  // TERMINAL_WIN, so any loss still ranks below every non-terminal position
  // (the AI always prefers a real fighting chance), and the ply bias remains
  // a sub-tiebreak (lose later when the positional score is otherwise even).
  if (state.winner !== null) {
    const blend = weights.lossBlend !== false ? positional : 0;
    return -TERMINAL_WIN - depthRemaining * TERMINAL_PLY_BIAS + blend;
  }

  return positional;
}

/** Look up a ring-count curve, clamping indices past the end to the last
 *  entry (so a curve shorter than ringSlots still defines every count). */
function curveAt(curve: number[], count: number): number {
  if (curve.length === 0) return 0;
  return curve[Math.min(count, curve.length - 1)];
}
